import React, { useEffect, useState } from 'react';
import SEO from './SEO';
import OptimizedImage from './OptimizedImage';
import WhatsAppFloat from './WhatsAppFloat';
import { trackCTA } from '../utils/analytics';
import { getSegmentLandingPageSchema } from '../utils/structuredData';
import { modulos } from '../data/modulos';
import './LandingPage.css';

const WHATSAPP_NUMBER = '5543996782197';
const DEMO_VIDEO_URL = '';

const getWhatsAppUrl = (message = '') => {
  const encodedMessage = encodeURIComponent(message);
  return `https://wa.me/${WHATSAPP_NUMBER}${message ? `?text=${encodedMessage}` : ''}`;
};

const ADS_EVENTS = {
  WHATSAPP_HEADER: 'ads_conv_lp_comercio_whatsapp_header',
  WHATSAPP_CTA_FINAL: 'ads_conv_lp_comercio_whatsapp_cta_final',
  DOWNLOAD_CTA_FINAL: 'ads_conv_lp_comercio_download_cta_final',
  DOWNLOAD_FOOTER: 'ads_conv_lp_comercio_download_footer'
};

const LandingComercio = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const pdvModulo = modulos.pdvDelivery;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const handleWhatsAppClick = (e, message, eventName) => {
    e.preventDefault();
    const whatsappUrl = getWhatsAppUrl(message);
    if (window.gtagSendEvent) {
      window.gtagSendEvent(whatsappUrl, eventName);
    } else {
      window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleDownloadClick = (e, url, eventName) => {
    e.preventDefault();
    if (window.gtagSendEvent) {
      window.gtagSendEvent(url, eventName);
    } else {
      window.location.href = url;
    }
  };

  return (
    <div className="landing-page">
      <SEO
        title="ERP para Comércio com PDV | Veritux ERP"
        description="Sistema para pequenos comércios com foco em PDV, controle de caixa, vendas e estoque. Veja como padronizar a operação de loja e ganhar velocidade no balcão."
        keywords="ERP para comércio, sistema PDV, controle de caixa, ERP para loja física, gestão de estoque para varejo, fechamento de caixa"
        url="https://www.veritux.com.br/erp-para-comercio"
        structuredData={getSegmentLandingPageSchema('comercio', 'https://www.veritux.com.br/erp-para-comercio')}
      />

      <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
        <div className="container">
          <div className="header-content">
            <a href="/" className="logo">
              <img src="/Logo.ico" alt="Veritux ERP" className="logo-img" />
              <span className="logo-text">Veritux ERP</span>
            </a>
            <nav className="nav" aria-label="Navegação principal">
              <a href="#beneficios">Benefícios</a>
              <a href="#video">Demonstração</a>
              <a href="#faq">FAQ</a>
            </nav>
            <div className="header-cta">
              <a
                href={getWhatsAppUrl('Olá! Quero ver o Veritux ERP para comércio com foco em PDV.')}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
                onClick={(e) => {
                  trackCTA('whatsapp_comercio', 'header');
                  handleWhatsAppClick(e, 'Olá! Quero ver o Veritux ERP para comércio com foco em PDV.', ADS_EVENTS.WHATSAPP_HEADER);
                }}
              >
                Falar com especialista
              </a>
            </div>
            <button
              className="mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Abrir menu"
              aria-expanded={isMobileMenuOpen}
            >
              <span className={`hamburger ${isMobileMenuOpen ? 'active' : ''}`}>
                <span></span>
                <span></span>
                <span></span>
              </span>
            </button>
          </div>
        </div>
      </header>

      <div className={`mobile-menu-overlay ${isMobileMenuOpen ? 'active' : ''}`} onClick={() => setIsMobileMenuOpen(false)}></div>
      <nav className={`mobile-menu ${isMobileMenuOpen ? 'active' : ''}`} aria-label="Menu mobile">
        <div className="mobile-menu-content">
          <a href="#beneficios" onClick={() => setIsMobileMenuOpen(false)} className="mobile-menu-link">Benefícios</a>
          <a href="#video" onClick={() => setIsMobileMenuOpen(false)} className="mobile-menu-link">Demonstração</a>
          <a href="#faq" onClick={() => setIsMobileMenuOpen(false)} className="mobile-menu-link">FAQ</a>
        </div>
      </nav>

      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1 className="hero-title">
                ERP para pequeno comércio com <span className="highlight">PDV ágil</span> e <span className="highlight">caixa sob controle</span>
              </h1>
              <p className="hero-subtitle">
                Venda no balcão com mais velocidade e menos retrabalho no fechamento.
              </p>
              <p className="hero-description">
                O Veritux organiza rotina de venda, abertura e fechamento de caixa, controle de estoque e relatórios de PDV
                para você ter decisão rápida durante o expediente.
              </p>
              <div className="hero-cta">
                <a href="#demo" className="btn btn-primary btn-large">Quero ver o fluxo de PDV</a>
                <a href="#video" className="btn btn-secondary btn-large">Ver demo (comércio)</a>
              </div>
              <div className="hero-trust">
                <p className="trust-text">✓ Fluxo de caixa padronizado</p>
                <p className="trust-text">✓ Visão de vendas por operador</p>
                <p className="trust-text">✓ Estoque integrado ao balcão</p>
              </div>
            </div>
            <div className="hero-visual">
              <OptimizedImage
                src="/images/product/hero/dashboard"
                alt="Tela de gestão para comércio com foco em PDV e caixa"
                className="hero-dashboard-image"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      <section id="beneficios" className="benefits">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Feito para operação de loja física</h2>
            <p className="section-subtitle">Benefícios práticos para balcão, caixa e estoque</p>
          </div>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon">🛒</div>
              <h3 className="benefit-title">Venda PDV com processo único</h3>
              <p className="benefit-text">{pdvModulo.detalhes.pdv}</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">💵</div>
              <h3 className="benefit-title">Abertura, sangria e fechamento de caixa</h3>
              <p className="benefit-text">Controle de rotina de caixa com registro de sangria e suprimento para reduzir diferença no fechamento.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">📦</div>
              <h3 className="benefit-title">Estoque alinhado à venda</h3>
              <p className="benefit-text">A venda no PDV impacta estoque e facilita reposição com base em giro real dos produtos.</p>
            </div>
          </div>
        </div>
      </section>

      <section id="video" className="video-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Demonstração do PDV para comércio</h2>
            <p className="section-subtitle">Espaço preparado para vídeo da rotina de balcão</p>
          </div>
          <div className="video-container">
            {DEMO_VIDEO_URL ? (
              <div className="video-wrapper">
                <iframe
                  className="youtube-player"
                  src={DEMO_VIDEO_URL}
                  title="Demonstração do Veritux ERP para comércio"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
            ) : (
              <div className="segment-demo-placeholder">
                <h3>Demonstração PDV (em breve)</h3>
                <p>Substitua este bloco pelo vídeo com o fluxo real: abertura, venda, sangria e fechamento.</p>
                <a
                  href={getWhatsAppUrl('Olá! Quero agendar uma demonstração do fluxo de PDV para comércio.')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                  onClick={(e) => handleWhatsAppClick(e, 'Olá! Quero agendar uma demonstração do fluxo de PDV para comércio.', ADS_EVENTS.WHATSAPP_HEADER)}
                >
                  Agendar demonstração guiada
                </a>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="social-proof">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Programa Pioneiro — 20 vagas</h2>
            <p className="section-subtitle">
              O Veritux está abrindo as primeiras vagas para o comércio. Teste 15 dias grátis e entre
              com condição especial de Pioneiro para os 20 primeiros clientes.
            </p>
          </div>
          <p className="stats-footnote">
            Os resultados dependem do uso e do processo de cada loja. Quando tivermos clientes Pioneiros,
            traremos depoimentos reais e autorizados por escrito.
          </p>
        </div>
      </section>

      <section id="faq" className="faq-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Perguntas frequentes de comércio</h2>
          </div>
          <div className="faq-grid">
            <div className="faq-item">
              <h3 className="faq-question">Serve para loja com mais de um caixa?</h3>
              <p className="faq-answer">Sim. O fluxo suporta operação com múltiplos operadores de PDV e gestão de caixa por turno.</p>
            </div>
            <div className="faq-item">
              <h3 className="faq-question">Consigo acompanhar vendas por período e operador?</h3>
              <p className="faq-answer">Sim. Há relatórios de vendas do PDV por período, usuário e indicadores para análise de pico.</p>
            </div>
            <div className="faq-item">
              <h3 className="faq-question">O estoque atualiza com a venda no balcão?</h3>
              <p className="faq-answer">Sim. O processo integra vendas e estoque para reduzir divergências e apoiar reposição.</p>
            </div>
          </div>
        </div>
      </section>

      <section id="demo" className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Quer avaliar o Veritux no seu comércio?</h2>
            <p className="cta-subtitle">
              Teste grátis por 15 dias, sem cartão. Entre no <strong>Programa Pioneiro</strong>: R$ 49,90/mês
              nos 6 primeiros meses (depois R$ 197/mês, congelado por 12 meses) — limitado às 20 primeiras contratações.
            </p>
            <div className="cta-buttons">
              <a
                href={getWhatsAppUrl('Olá! Quero ver uma demonstração do Veritux para comércio com foco em PDV.')}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary btn-large"
                onClick={(e) => {
                  trackCTA('whatsapp_demo_comercio', 'cta_final');
                  handleWhatsAppClick(e, 'Olá! Quero ver uma demonstração do Veritux para comércio com foco em PDV.', ADS_EVENTS.WHATSAPP_CTA_FINAL);
                }}
              >
                Agendar conversa no WhatsApp
              </a>
              <a
                href="/downloads"
                rel="noopener noreferrer"
                className="btn btn-secondary btn-large"
                onClick={(e) => {
                  trackCTA('download_instalador_comercio', 'cta_final');
                  handleDownloadClick(e, '/downloads', ADS_EVENTS.DOWNLOAD_CTA_FINAL);
                }}
              >
                Baixar instalador e começar a usar
              </a>
            </div>
            <p className="segment-switch-link">
              Sua empresa é de manutenção ou assistência técnica? <a href="/erp-para-servicos">Veja a landing de serviços</a>.
            </p>
          </div>
        </div>
      </section>

      <footer className="footer">
        <div className="container">
          <div className="footer-bottom">
            <p className="footer-copyright">
              Desenvolvido por <a href="https://mngd.com.br" target="_blank" rel="noopener noreferrer">MNGD Sistemas</a> &copy; 2026 Veritux ERP.
            </p>
            <div className="footer-legal">
              <a href="/funcionalidades">Funcionalidades</a>
              <span className="footer-separator">•</span>
              <a href="/erp-para-servicos">Landing serviços</a>
              <span className="footer-separator">•</span>
              <a
                href="/downloads"
                onClick={(e) => handleDownloadClick(e, '/downloads', ADS_EVENTS.DOWNLOAD_FOOTER)}
              >
                Downloads
              </a>
            </div>
          </div>
        </div>
      </footer>

      <WhatsAppFloat />
    </div>
  );
};

export default LandingComercio;
